import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { ClipboardEvent, useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { PAGE_SIZE } from 'api/canonicalPicker'
import type {
  PickerRow,
  PickerRowKind,
  PickerRowsPage,
  UidGroupRow,
} from 'api/canonicalPicker.types'
import PastedUidsChip from 'components/AI/CanonicalPicker/PastedUidsChip'
import palette from 'constants/palette'
import {
  Icon,
  NavButton,
  PaginationControls,
  PaginationInfo,
  Select,
  SelectAllToggle,
  SelectHeader,
  SelectMenu,
  SelectTrigger,
} from 'controlled-components'
import { buildTranslate } from 'locales'
import type { PickerSource } from 'slices/aiConversations/pickerTypes'
import { picksOf, SourceSelection } from 'slices/aiSegmentBuilder/picker'

import CanonicalOptionsContainer from './CanonicalOptions.container'
import { looksLikeUid } from './pastedUids'
import { picksFirst } from './picksFirst'
import selectionSummary from './selectionSummary'
import UidSearchResultsContainer from './UidSearchResults.container'
import { toggledGroup, toggledPage } from './uidTree'
import useLevelRows from './useLevelRows'
import useScopedUidSearch from './useScopedUidSearch'
import useUidSearch, { UidPaste } from './useUidSearch'

const t = buildTranslate('components.ai.picker')

const SEARCH_DEBOUNCE_MS = 300

const ALL_DIGITS = /^\d+$/

/**
 * Whether a typed number's name search answers it: its UID lookup holds no
 * group, and the names match something or are still loading. Only when
 * neither finds anything does the UID lookup offer to add it.
 */
export const namesAnswerFor = (
  uidSearch: UidPaste,
  names: PickerRowsPage | undefined
): boolean =>
  uidSearch.status === 'checked' &&
  uidSearch.groups.length === 0 &&
  (names === undefined || names.rows.length > 0)

type Props = {
  source: PickerSource
  kind: PickerRowKind
  selection: SourceSelection
  /** Canonical ids the source is filtered to; an empty scope leaves nothing to pick */
  scope: string[] | null
  /** For a fixed list on level 2: the smart source that names its products under level 1 */
  scopeSource?: PickerSource | null
  /** Heads the searched UIDs that exist outside a scoped level */
  outOfScopeLabel?: string
  placeholder: string
  disabled: boolean
  onPick: (row: PickerRow) => void
  /** Replaces the picks, for selecting or clearing a whole page */
  onSetPicks: (rows: PickerRow[]) => void
  onClear: () => void
}

/**
 * The dropdown a canonical level picks from, one page of its source at a
 * time, with what is already picked pinned on top.
 */
const CanonicalRowsSelectContainer = ({
  source,
  kind,
  selection,
  scope,
  scopeSource = null,
  outOfScopeLabel = '',
  placeholder,
  disabled,
  onPick,
  onSetPicks,
  onClear,
}: Props) => {
  const [css] = useStyletron()
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pickedWhenOpened, setPickedWhenOpened] = useState<PickerRow[]>([])
  const [debouncedSearch] = useDebounce(search, SEARCH_DEBOUNCE_MS)

  const picks = useMemo(() => picksOf(selection), [selection])
  const isMultiselect = source.selectionType === 'multiselect'
  const isUnavailable = disabled || scope?.length === 0
  // Only a fixed list is searched by UID; a smart list's search is by name
  const canPasteUids = kind === 'uidGroup' && isMultiselect

  const addPicks = (rows: PickerRow[]) => {
    const added = rows.filter(row => !(row.id in selection.rows))
    if (added.length > 0) onSetPicks([...picks, ...added])
  }

  const term = debouncedSearch.trim()
  const typedUid = canPasteUids && isOpen && looksLikeUid(term) ? term : null
  const {
    search: uidSearch,
    isPaste,
    pasteText,
    clearPaste,
    retry,
    isRetrying,
  } = useUidSearch(source, typedUid)
  // A typed number can be part of a name as well as a UID, so it searches both
  const isTypedNumber =
    !isPaste && typedUid !== null && ALL_DIGITS.test(typedUid)

  // currentData, so the last search's rows never show while the next loads
  const { currentData, isFetching, isError, refetch } = useLevelRows({
    source,
    kind,
    search: debouncedSearch,
    page,
    scope,
    scopeSource,
    skip: !isOpen || (uidSearch !== null && !isTypedNumber),
  })
  const scopedSearch = useScopedUidSearch({
    search: uidSearch,
    source,
    kind,
    scope,
    scopeSource,
    retrySearch: retry,
  })
  const showsUidSearch =
    scopedSearch.search !== null &&
    !(isTypedNumber && namesAnswerFor(scopedSearch.search, currentData))

  const pageRows = useMemo(() => currentData?.rows ?? [], [currentData])
  const rows = useMemo(
    () => picksFirst(pageRows, pickedWhenOpened, selection, debouncedSearch),
    [pageRows, pickedWhenOpened, selection, debouncedSearch]
  )

  const open = () => {
    setPickedWhenOpened(picks)
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setSearch('')
    setPage(1)
  }

  const clearPicks = () => {
    clearPaste()
    onClear()
  }

  const changeSearch = (value: string) => {
    setSearch(value)
    setPage(1)
    setPickedWhenOpened(picks)
    clearPaste()
  }

  const pasteUids = (event: ClipboardEvent<HTMLDivElement>) => {
    if (!canPasteUids || !pasteText(event.clipboardData.getData('text'))) {
      return
    }
    event.preventDefault()
    setSearch('')
    setPage(1)
  }

  const pick = (row: PickerRow) => {
    onPick(row)
    if (!isMultiselect) close()
  }

  const pickedOnPage = pageRows.filter(row => row.id in selection.rows)
  const isPageAllPicked =
    pageRows.length > 0 && pickedOnPage.length === pageRows.length
  const isPagePartlyPicked = pickedOnPage.length > 0 && !isPageAllPicked

  const togglePage = () => onSetPicks(toggledPage(picks, pageRows))
  const pickGroup = (group: UidGroupRow) =>
    onSetPicks(toggledGroup(picks, group))

  const totalCount = currentData?.totalCount ?? 0
  const lastOnPage = Math.min(page * PAGE_SIZE, totalCount)
  const pageInfo =
    totalCount > 0
      ? t('canonical.page_info', {
          first: (page - 1) * PAGE_SIZE + 1,
          last: lastOnPage,
          total: totalCount,
        })
      : t('canonical.page_info_empty')
  const summary = selectionSummary(picks)

  const renderHeaderLeft = () => {
    if (uidSearch && isPaste) {
      return (
        <PastedUidsChip
          pastedCount={uidSearch.pastedCount}
          onClear={clearPaste}
        />
      )
    }
    // Nothing to select on an empty page
    if (!isMultiselect || pageRows.length === 0) return null
    return (
      <SelectAllToggle
        checked={isPageAllPicked}
        indeterminate={isPagePartlyPicked}
        onToggle={togglePage}
      />
    )
  }

  const renderBody = () => {
    if (scopedSearch.search && showsUidSearch) {
      return (
        <UidSearchResultsContainer
          key={scopedSearch.search.uids.join(' ')}
          paste={scopedSearch.search}
          outOfScope={scopedSearch.outOfScope}
          outOfScopeLabel={outOfScopeLabel}
          isTyped={!isPaste}
          source={source}
          selection={selection}
          isRetrying={isRetrying}
          onRetry={scopedSearch.retry}
          onPick={pick}
          onPickGroup={pickGroup}
          onAdd={addPicks}
        />
      )
    }
    if (isError) {
      return (
        <div role="alert" className="p-m text-center">
          <Text.BodyText4 tag="p" color="grey70">
            {t('canonical.rows_error')}
          </Text.BodyText4>
          <Button kind="minimal" isLoading={isFetching} onClick={refetch}>
            {t('canonical.retry')}
          </Button>
        </div>
      )
    }
    if (!currentData) {
      return (
        <div className="p-m text-center">
          <Icon name="spinner" size={24} spin />
        </div>
      )
    }
    if (rows.length === 0 && term !== '') {
      return (
        <div role="status" className="p-m text-center">
          <Text.BodyText4 color="grey70">{t('no_matches')}</Text.BodyText4>
        </div>
      )
    }
    return (
      <CanonicalOptionsContainer
        key={`${term}:${page}`}
        source={source}
        rows={rows}
        selection={selection}
        isMultiselect={isMultiselect}
        onPick={pick}
        onPickGroup={pickGroup}
      />
    )
  }

  return (
    <Select isOpen={isOpen} onClose={close} disabled={isUnavailable}>
      <div className="position-relative">
        <SelectTrigger
          role="combobox"
          aria-label={placeholder}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          placeholder={placeholder}
          // The summary is cut off with an ellipsis; this keeps all of it
          title={summary || undefined}
          isOpen={isOpen}
          disabled={isUnavailable}
          onToggle={() => (isOpen ? close() : open())}
          searchValue={search}
          onSearchChange={changeSearch}
          onPaste={pasteUids}
          isLoading={isFetching}
          showClear
          onClear={clearPicks}
        >
          {summary || null}
        </SelectTrigger>
        <SelectMenu
          isOpen={isOpen && !isUnavailable}
          role="listbox"
          aria-label={placeholder}
          aria-multiselectable={isMultiselect}
        >
          <SelectHeader>
            <SelectHeader.Left>{renderHeaderLeft()}</SelectHeader.Left>
            <SelectHeader.Right>
              {!showsUidSearch && (
                <>
                  <PaginationInfo>{pageInfo}</PaginationInfo>
                  <PaginationControls>
                    <NavButton
                      direction="prev"
                      disabled={page === 1}
                      onClick={() => setPage(page - 1)}
                    />
                    <NavButton
                      direction="next"
                      disabled={lastOnPage >= totalCount}
                      onClick={() => setPage(page + 1)}
                    />
                  </PaginationControls>
                </>
              )}
            </SelectHeader.Right>
          </SelectHeader>
          {renderBody()}
          {canPasteUids && (
            <div
              className={`px-m py-s ${css({
                borderTop: `1px solid ${palette.grey20}`,
              })}`}
            >
              <Text.BodyText5 color="grey70">
                {t('canonical.footer.specific')}
              </Text.BodyText5>
            </div>
          )}
        </SelectMenu>
      </div>
    </Select>
  )
}

export default CanonicalRowsSelectContainer
